import { Fragment, useCallback, useEffect, useState } from "react";
import { fetchTranslations } from "../../utils/LOGIE_API";
import { parseTextWithLinks } from "../../utils/helper";

const encodeForTranslation = (value) => {
  if (typeof value !== "string" || value === "")
    return { encodedText: value, specialCharsMap: [] };
  // Create a map to store original positions of special characters
  const specialCharsMap = [];

  return {
    encodedText: value.replace(/[\n\t]/g, (match, offset) => {
      const token = `[${match === "\n" ? "NL" : "TAB"}_${offset}]`;
      specialCharsMap.push({ token, char: match });
      return token;
    }),
    specialCharsMap,
  };
};

const decodeFromTranslation = (value, specialCharsMap) => {
  if (specialCharsMap.length === 0) {
    return value;
  }

  let decodedText = value;

  // Sort by token length (longest first) to avoid partial replacements
  specialCharsMap.sort((a, b) => b.token.length - a.token.length);

  // Replace each token with its original character
  specialCharsMap.forEach(({ token, char }) => {
    decodedText = decodedText.replace(token, char);
  });

  return decodedText;
};

const translationCache = new Map();

const useFieldTranslation = (feature, fieldName, t, i18n) => {
  const [translation, setTranslation] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);

  useEffect(() => {
    let timer;
    const layer = feature?.sourceLayer ?? feature?.layer;
    const translatedFields =
      layer?.layerConfig?.customPopupOps?.translatedFields;
    let controller;

    const objectId = feature.getObjectId();
    const lang = i18n.language === "en" ? "en-US" : i18n.language;
    const cacheKey = `${objectId}_${fieldName}_${lang}`;

    if (
      !translatedFields ||
      !Array.isArray(translatedFields) ||
      !translatedFields.includes(fieldName) ||
      (feature.attributes && !feature.attributes[fieldName])
    )
      return;

    // Check cache first
    if (translationCache.has(cacheKey)) {
      setTranslation(translationCache.get(cacheKey));
      setShowTranslation(true);
      setIsTranslating(false);
      return;
    }

    timer = setTimeout(() => {
      setIsTranslating(true);
      setTranslation(null);
      setShowTranslation(false);
      controller = new AbortController();
      const signal = controller.signal;

      const value = feature.attributes[fieldName];
      const { encodedText: encoded, specialCharsMap } =
        encodeForTranslation(value);

      const url_params = new URLSearchParams({
        layerId: layer.originalId,
        objectId: feature.attributes[layer.objectIdField],
        fieldName: fieldName,
        value: encoded,
        lang,
      }).toString();

      fetchTranslations(url_params, { signal })
        .then((response) => {
          if (response.ok) return response.text();
        })
        .then((data) => {
          setIsTranslating(false);
          const trans = JSON.parse(data);

          if (
            trans.statusCode === 200 &&
            trans.detectedSourceLang !== i18n.language
          ) {
            const decodeValue = decodeFromTranslation(
              trans.body,
              specialCharsMap
            );

            setTranslation(decodeValue);
            setShowTranslation(true);

            // Save to cache
            translationCache.set(cacheKey, decodeValue);
          }
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            // console.warn("Translations Fetch aborted");
          } else {
            console.log(error.name);
            setIsTranslating(false);
            console.error("Error occurred fetching translations:", error);
          }
        });
    }, 350);

    return () => {
      clearTimeout(timer);
      if (controller) {
        controller.abort();
      }
    };
  }, [feature, t, fieldName, i18n.language]);

  const renderTextWithLinks = useCallback((text) => {
    if (!text) return [];

    const textWithLink = parseTextWithLinks(text);
    return textWithLink.map((part, idx) => {
      if (typeof part === "string") {
        // Split by \n and render with <br />
        return part.split("\n").map((line, i, arr) => (
          <Fragment key={`${idx}-${i}`}>
            {line}
            {i < arr.length - 1 && <br />}
          </Fragment>
        ));
      }
      return part;
    });
  }, []);

  const toggleTranslation = (e) => {
    e.stopPropagation();
    setShowTranslation((prev) => !prev);
  };

  return {
    translation,
    isTranslating,
    showTranslation,
    setShowTranslation,
    setTranslation,
    setIsTranslating,
    renderTextWithLinks,
    toggleTranslation,
  };
};

export default useFieldTranslation;
